import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasFinePointer, prefersReducedMotion } from './effects/motion-prefs';
import { Countdown } from './countdown';
import { LANDING_DATES } from './landing-config';
import { LockedBadge } from './locked-badge';
import { useParticipateCta } from './participate-cta';
import { NotEligibleNotice, type NotEligibleReason } from './not-eligible';

interface HeroProps {
  /** null — баланс не показываем (участие не подтверждено). */
  balance: number | null;
  participationConfirmed: boolean;
  /** Не участвует в акции — уведомление вместо кнопки и подписи о козочках. */
  notEligible: NotEligibleReason | null;
}

/** Первый экран (`section.hero` макета): заголовок, таймер до конца начисления, CTA. */
export function Hero({ balance, participationConfirmed, notEligible }: HeroProps) {
  const { t } = useTranslation('landing');
  const cardRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);
  const bokehRef = useRef<HTMLDivElement>(null);
  const cta = useParticipateCta();

  // Параллакс фото в hero-карточке по движению мыши (js/main.js, «hero mouse parallax»).
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !hasFinePointer() || prefersReducedMotion()) return;
    const onMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      if (photoRef.current) photoRef.current.style.transform = `translate(${x * -18}px,${y * -10}px) scale(1.04)`;
      if (bokehRef.current) bokehRef.current.style.transform = `translate(${x * 30}px,${y * 20}px)`;
    };
    const onLeave = () => {
      if (photoRef.current) photoRef.current.style.transform = '';
      if (bokehRef.current) bokehRef.current.style.transform = '';
    };
    card.addEventListener('mousemove', onMove as never);
    card.addEventListener('mouseleave', onLeave);
    return () => {
      card.removeEventListener('mousemove', onMove as never);
      card.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <section className="hero">
      <div className="wrap">
        <div className="datepill rv">{t('hero.datePill')}</div>
        <div className="cardwrap rv d1">
          <div className="hcard" ref={cardRef}>
            <div className="bokeh" ref={bokehRef} />
            <div className="photo" ref={photoRef} />
            <i className="star" style={{ ['--s' as string]: '90px', left: '36%', top: '8%', ['--t' as string]: '4.5s' }} />
            <i className="star intext" style={{ ['--s' as string]: '70px', left: '50%', top: '70%', ['--t' as string]: '3.6s' }} />
            <i className="star" style={{ ['--s' as string]: '56px', right: '6%', top: '22%', ['--t' as string]: '5s' }} />
            <i className="star intext" style={{ ['--s' as string]: '40px', left: '46%', top: '86%', ['--t' as string]: '3s' }} />
            <div className="brand">
              <div className="brandrow">
                <i className="ico i-nl" />
                <i className="dia" />
                <div className="year">
                  <span>2027</span>
                </div>
              </div>
              <h1 className="headline rv">
                <span className="w">
                  <span>{t('hero.headline.line1Word1')}</span>
                </span>{' '}
                <span className="w">
                  <span>{t('hero.headline.line1Word2')}</span>
                </span>
                <br />
                <span className="w">
                  <span>{t('hero.headline.line2Word1')}</span>
                </span>{' '}
                <span className="w">
                  <span className="grad">{t('hero.headline.line2Word2')}</span>
                </span>
              </h1>
              <p className="lead">
                {t('hero.lead')} <br />
                <strong>{t('hero.leadStrong')}</strong>
              </p>
              <Countdown targetIso={LANDING_DATES.accrualEnd} />
              <div className="cdlabel">{t('hero.countdownLabel')}</div>
              <div className="ctawrap">
                {notEligible && <NotEligibleNotice reason={notEligible} />}
                {!participationConfirmed && !notEligible && (
                  <a href={cta.href} onClick={cta.onClick} className="btn cta oncard magnet" aria-busy={cta.pending}>
                    <i className="ico i-goat" />
                    <span>{t('hero.cta')}</span>
                    <span className="ar">→</span>
                  </a>
                )}
                {!notEligible && (
                  <div className="ctanote">
                    {participationConfirmed && balance !== null ? (
                      <a className="notelink" href="#gifts">
                        <b>{t('hero.ctaNote', { count: balance })}</b> <span className="ar">→</span>
                      </a>
                    ) : (
                      <LockedBadge hint />
                    )}
                  </div>
                )}
                <Link className="rulesbtn light" to="/rules" data-rules="">
                  <svg className="docic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                    <path d="M14 3v5h5M9 13h6M9 17h4" />
                  </svg>
                  {t('hero.rulesLink')}
                </Link>
              </div>
            </div>
          </div>
          <div className="stamp" aria-hidden="true">
            <svg viewBox="0 0 100 100">
              <defs>
                <path id="circ" d="M50,50 m-30,0 a30,30 0 1,1 60,0 a30,30 0 1,1 -60,0" />
              </defs>
              <polygon
                fill="#951E30"
                points="100.00,50.00 96.21,55.21 98.75,61.13 93.89,65.36 95.05,71.69 89.37,74.74 89.09,81.17 82.88,82.88 81.17,89.09 74.74,89.37 71.69,95.05 65.36,93.89 61.13,98.75 55.21,96.21 50.00,100.00 44.79,96.21 38.87,98.75 34.64,93.89 28.31,95.05 25.26,89.37 18.83,89.09 17.12,82.88 10.91,81.17 10.63,74.74 4.95,71.69 6.11,65.36 1.25,61.13 3.79,55.21 0.00,50.00 3.79,44.79 1.25,38.87 6.11,34.64 4.95,28.31 10.63,25.26 10.91,18.83 17.12,17.12 18.83,10.91 25.26,10.63 28.31,4.95 34.64,6.11 38.87,1.25 44.79,3.79 50.00,0.00 55.21,3.79 61.13,1.25 65.36,6.11 71.69,4.95 74.74,10.63 81.17,10.91 82.88,17.12 89.09,18.83 89.37,25.26 95.05,28.31 93.89,34.64 98.75,38.87 96.21,44.79"
              />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#FBF4E6" strokeOpacity={0.25} strokeWidth={0.6} strokeDasharray="1.5 2" />
              <text fill="#FBF4E6" fontFamily="Montserrat" fontWeight={800} fontSize={8.2}>
                <textPath href="#circ" textLength={186} lengthAdjust="spacing">
                  NL 2027 · NL 2027 · NL 2027 ·
                </textPath>
              </text>
            </svg>
            <i className="ico i-goat" />
          </div>
        </div>
      </div>
    </section>
  );
}
